import { useP2GDebounce } from '@parcel2go/core-ui';
import { createContext, ReactNode, useEffect, useMemo, useReducer } from 'react';

export interface Download {
  UID?: string;
  Label: string;
  Type: string;
  Href: string;
  Status: 'Processing' | 'Ready' | '';
}

export interface IDownloadContextAction {
  type: 'ADD' | 'REFRESH' | 'REMOVE';
  payload?: any;
}

export function DownloadContextReducer(state: Download[], action: IDownloadContextAction) {
  switch (action.type) {
    case 'ADD':
      return [action.payload, ...state];
    case 'REMOVE':
      return [...state.filter((x) => x.UID !== action.payload)];
    case 'REFRESH': {
      const update = [...state];
      update.forEach((x) => {
        x.Status = 'Ready';
      });
      return update;
    }
    default:
      return state;
  }
}

const initialState: Download[] = JSON.parse(sessionStorage.getItem('SS_DOWNLOADS') || '[]');

const DownloadContext = createContext<{
  state: typeof initialState;
  dispatch: (action: IDownloadContextAction) => void;
}>({
  state: initialState,
  dispatch: () => {}
});

const DownloadContextProvider: React.FC<{ children: ReactNode }> = (props) => {
  const [state, dispatch] = useReducer(DownloadContextReducer, initialState);
  const value = { state, dispatch };

  const toUpdate = useMemo(() => {
    const downloads = value.state as Download[];
    return downloads
      .filter((x) => x.Status === 'Processing')
      .map((x) => x.UID)
      .join('_');
  }, [value.state.length]);

  const [d, setD] = useP2GDebounce('', 2500);

  useEffect(() => {
    setD(toUpdate);
  }, [toUpdate]);

  useEffect(() => {
    try {
      sessionStorage.setItem('SS_DOWNLOADS', JSON.stringify(value.state.slice(0, 50)));
    } catch {
      // Ignore errors
    }
  }, [value.state.length]);

  useEffect(() => {
    dispatch({ type: 'REFRESH', payload: null });
    try {
      sessionStorage.setItem('SS_DOWNLOADS', JSON.stringify(value.state.slice(0, 50)));
    } catch {
      // Ignore errors
    }
  }, [d]);

  return <DownloadContext.Provider value={value}>{props.children}</DownloadContext.Provider>;
};

const ContextConsumer = DownloadContext.Consumer;

export { DownloadContext, DownloadContextProvider, ContextConsumer };
